import { MatrixClient, Room, RoomEvent, RoomMember, RoomStateEvent, User, UserEvent } from 'matrix-js-sdk/src/matrix';
import { useEffect, useRef, useState } from 'react';

import { fetchMediaAsBlobUrl } from 'utils/matrix/matrixMediaUtils';

export const resolveMxcAvatarUri = (
  matrixClient?: MatrixClient | null,
  room?: Room | null,
  userId?: string
): string | undefined => {
  if (!matrixClient || !userId) return;

  const userMxc = matrixClient.getUser(userId)?.avatarUrl;
  if (userMxc) return userMxc;

  const memberMxc = room?.getMember(userId)?.getMxcAvatarUrl();
  if (memberMxc) return memberMxc;

  return;
};

const useMatrixUserAvatarBlobUrl = (
  matrixClient?: MatrixClient | null,
  userId?: string,
  room?: Room | null
): string | undefined => {
  const [blobUrl, setBlobUrl] = useState<string | undefined>();
  const blobUrlRef = useRef<string | undefined>();
  const loadSeqRef = useRef(0);

  useEffect(() => {
    if (!matrixClient || !userId) {
      loadSeqRef.current += 1;
      if (blobUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = undefined;
      }
      return setBlobUrl(undefined);
    }

    const applyBlobUrl = (url?: string) => {
      if (blobUrlRef.current?.startsWith('blob:') && blobUrlRef.current !== url) {
        URL.revokeObjectURL(blobUrlRef.current);
      }

      blobUrlRef.current = url;
      setBlobUrl(url);
    };

    const runLoad = async () => {
      const requestId = ++loadSeqRef.current;

      const mxcUri = resolveMxcAvatarUri(matrixClient, room ?? undefined, userId);

      if (!mxcUri) {
        if (requestId !== loadSeqRef.current) return;
        return applyBlobUrl(undefined);
      }

      const url = await fetchMediaAsBlobUrl(matrixClient, mxcUri, {
        width: 96,
        height: 96,
        method: 'crop',
      });

      if (requestId !== loadSeqRef.current) return;

      applyBlobUrl(url ?? undefined);
    };

    runLoad();

    const onUserAvatar = (_event: unknown, user: User) => {
      if (user.userId === userId) runLoad();
    };

    const onRoomMemberUpdated = (_event: unknown, _state: unknown, member: RoomMember) => {
      if (member.userId === userId) runLoad();
    };

    room?.on(RoomEvent.CurrentStateUpdated, runLoad);
    room?.on(RoomStateEvent.Members, onRoomMemberUpdated);
    matrixClient.on(UserEvent.AvatarUrl, onUserAvatar);

    return () => {
      loadSeqRef.current += 1;
      room?.off(RoomEvent.CurrentStateUpdated, runLoad);
      room?.off(RoomStateEvent.Members, onRoomMemberUpdated);
      matrixClient.off(UserEvent.AvatarUrl, onUserAvatar);
    };
  }, [matrixClient, room, userId]);

  return blobUrl;
};

export default useMatrixUserAvatarBlobUrl;
